import { Box } from "@mui/material";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IProfileDashboardPanel } from "../../../../../models/profile-dashboard-panel";
import MainApi from "../../../../../services/mainApi.service";
import { deleteRoleDashboardPanel } from "../../../../../services/profileDashboardPanels.service";
import { ProfileDashboardPanel } from "./ProfileDashboardPanelForm/ProfileDashboardPanel";

const PROFILE_DASHBOARD_PANELS_ROUTE =
  process.env.REACT_APP_IDENTIY + "/api/v1/ProfileDashboardPanels";

const translationPath = "page.register.roles.";

const ProfileDashboardPanelsPage = () => {
  const { id: roleId } = useParams();
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [rows, setRows] = useState<IProfileDashboardPanel[]>([]);

  const profileDashboardPanelsService = useMemo(() => {
    const service = new MainApi<IProfileDashboardPanel>(
      PROFILE_DASHBOARD_PANELS_ROUTE,
    ) as MainApi<IProfileDashboardPanel> & {
      deleteItemById: (id: string) => Promise<unknown>;
    };

    service.deleteItemById = (id: string) => deleteRoleDashboardPanel(id);

    return service;
  }, []);

  const columns: GridColDef<IProfileDashboardPanel>[] = [
    {
      field: "dashboardPanel",
      headerName: t(translationPath + "dashboard") || "Dashboard",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
  ];

  const handleAdd = () => {
    setSelectedId(undefined);
    setShowForm(true);
  };

  const handleRowClick = (params: GridRowParams<IProfileDashboardPanel>) => {
    if (!params.row) return;
    setSelectedId(params.row.id);
    setShowForm(true);
  };

  const handleBack = () => {
    setSelectedId(undefined);
    setShowForm(false);
  };

  const params = useMemo(
    () => ({
      RoleId: roleId,
    }),
    [roleId],
  );

  if (!roleId) return null;

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {showForm ? (
        <Box
          sx={(theme) => ({
            p: 1,
            height: "100%",
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
            borderRadius: 1,
          })}
        >
          <ProfileDashboardPanel
            id={selectedId}
            roleId={roleId}
            rows={rows}
            onBack={handleBack}
          />
        </Box>
      ) : (
        <Box height={600}>
          <Table<IProfileDashboardPanel>
            showDefaultMacroFilters={false}
            service={profileDashboardPanelsService}
            serviceProps={params}
            columns={columns}
            title={t(translationPath + "dashboards_grid_title") || "Dashboards"}
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            onFetchData={(data) => {
              setRows(data);
            }}
            defaultPageSize={20}
            pageSizeOptions={[5, 10, 20]}
            confirmOptions={{
              title:
                t(translationPath + "linked_dashboard_remove_title") ||
                "Confirm Deletion",
              description: t(
                translationPath + "linked_dashboard_remove_description",
              ),
              confirmationButtonProps: {
                variant: "contained",
                color: "error",
              },
            }}
            searchInputPlaceHolder={
              t(translationPath + "search_dashboard") || "Search Dashboard..."
            }
            addButtonLabel={t(translationPath + "add_dashboard") || "Add Dashboard"}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(ProfileDashboardPanelsPage);
